// Local storage utilities for OpenAI API key management
// Provides secure local storage for user's OpenAI API keys

const STORAGE_KEY = 'hamstudent-openai-api-key';

// Simple obfuscation for basic security (not cryptographic security)
const obfuscationKey = 'hamstudent-2024-voice-assessment';

// Simple XOR-based obfuscation (reversible)
const obfuscateString = (str) => {
  if (!str) return '';
  let result = '';
  for (let i = 0; i < str.length; i++) {
    const charCode = str.charCodeAt(i) ^ obfuscationKey.charCodeAt(i % obfuscationKey.length);
    result += String.fromCharCode(charCode);
  }
  return btoa(result); // Base64 encode for safe storage
};

// Reverse the obfuscation
const deobfuscateString = (obfuscatedStr) => {
  if (!obfuscatedStr) return '';
  try {
    const decoded = atob(obfuscatedStr); // Base64 decode
    let result = '';
    for (let i = 0; i < decoded.length; i++) {
      const charCode = decoded.charCodeAt(i) ^ obfuscationKey.charCodeAt(i % obfuscationKey.length);
      result += String.fromCharCode(charCode);
    }
    return result;
  } catch (error) {
    console.error('Failed to deobfuscate API key:', error);
    return '';
  }
};

// Check if localStorage is available
const isLocalStorageAvailable = () => {
  try {
    const test = '__localStorage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (e) {
    return false;
  }
};

// Validate OpenAI API key format
export const validateApiKey = (apiKey) => {
  if (!apiKey || typeof apiKey !== 'string') {
    return { isValid: false, error: 'API key is required' };
  }

  const trimmedKey = apiKey.trim();
  
  if (!trimmedKey.startsWith('sk-')) {
    return { isValid: false, error: 'Invalid API key format. OpenAI API keys should start with "sk-"' };
  }

  if (trimmedKey.length < 20) {
    return { isValid: false, error: 'API key appears to be too short. Please verify your API key' };
  }

  if (trimmedKey === 'sk-' || trimmedKey === 'your_openai_api_key_here') {
    return { isValid: false, error: 'Please enter your actual OpenAI API key' };
  }

  return { isValid: true, error: null };
};

// Save API key to localStorage
export const saveApiKey = (apiKey) => {
  if (!isLocalStorageAvailable()) {
    console.warn('localStorage not available - cannot save API key');
    return { success: false, error: 'Local storage is not available in your browser' };
  }

  // Validate the API key first
  const validation = validateApiKey(apiKey);
  if (!validation.isValid) {
    return { success: false, error: validation.error };
  }

  try {
    const obfuscatedKey = obfuscateString(apiKey.trim());
    const storageData = {
      key: obfuscatedKey,
      timestamp: Date.now(),
      version: '1.0'
    };
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(storageData));
    return { success: true, error: null };
  } catch (error) {
    console.error('Failed to save API key:', error);
    return { success: false, error: 'Failed to save API key to local storage' };
  }
};

// Load API key from localStorage
export const loadApiKey = () => {
  if (!isLocalStorageAvailable()) {
    return null;
  }

  try {
    const storedData = localStorage.getItem(STORAGE_KEY);
    if (!storedData) {
      return null;
    }

    const parsedData = JSON.parse(storedData);
    if (!parsedData.key) {
      return null;
    }

    const deobfuscatedKey = deobfuscateString(parsedData.key);
    
    // Validate the retrieved key
    const validation = validateApiKey(deobfuscatedKey);
    if (!validation.isValid) {
      console.warn('Stored API key is invalid, clearing storage');
      clearApiKey();
      return null;
    }

    return deobfuscatedKey;
  } catch (error) {
    console.error('Failed to load API key:', error);
    clearApiKey(); // Clean up corrupted data
    return null;
  }
};

// Check if API key exists and is valid
export const hasValidApiKey = () => {
  const apiKey = loadApiKey();
  if (!apiKey) return false;
  
  const validation = validateApiKey(apiKey);
  return validation.isValid;
};

// Clear saved API key
export const clearApiKey = () => {
  if (!isLocalStorageAvailable()) {
    return { success: false, error: 'Local storage is not available' };
  }

  try {
    localStorage.removeItem(STORAGE_KEY);
    return { success: true, error: null };
  } catch (error) {
    console.error('Failed to clear API key:', error);
    return { success: false, error: 'Failed to clear API key from local storage' };
  }
};

// Get masked version of API key for display (e.g., "sk-proj-****...****1234")
export const getMaskedApiKey = () => {
  const apiKey = loadApiKey();
  if (!apiKey) return null;

  if (apiKey.length <= 10) {
    return '****';
  }

  const start = apiKey.substring(0, 7); // "sk-proj" or similar
  const end = apiKey.substring(apiKey.length - 4); // last 4 characters
  const middle = '*'.repeat(Math.min(12, apiKey.length - 11)); // masked middle
  
  return `${start}${middle}${end}`;
};

// Get timestamp when API key was saved
export const getApiKeyTimestamp = () => {
  if (!isLocalStorageAvailable()) {
    return null;
  }

  try {
    const storedData = localStorage.getItem(STORAGE_KEY);
    if (!storedData) {
      return null;
    }

    const parsedData = JSON.parse(storedData);
    return parsedData.timestamp ? new Date(parsedData.timestamp) : null;
  } catch (error) {
    console.error('Failed to get API key timestamp:', error);
    return null;
  }
};

export default {
  saveApiKey,
  loadApiKey,
  hasValidApiKey,
  clearApiKey,
  validateApiKey,
  getMaskedApiKey,
  getApiKeyTimestamp
};